import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../lib/apiClient';
import { useAuth } from '../auth/useAuth';
import { IconButton } from '../components/IconButton';
import { IconTrash } from '../components/icons';
import { Modal } from '../components/Modal';
import MultiListSelect from '../components/MultiListSelect';
import Pagination from '../components/Pagination';
import { labelFor, listOptions, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { formatDateTime } from '../utils/format';
import { roleLabel } from '../utils/roles';
import vnpfLogo from '../assets/vnpf-logo.png';

const TABS = [
  'profile',
  'address',
  'nationalities',
  'languages',
  'identities',
  'password',
  'security',
  'notifications',
  'login-history',
] as const;
type Tab = (typeof TABS)[number];

const TAB_LABELS: Record<Tab, string> = {
  profile: 'Details',
  address: 'Address',
  nationalities: 'Nationalities',
  languages: 'Languages',
  identities: 'Identities',
  password: 'Password',
  security: 'Security',
  notifications: 'Notifications',
  'login-history': 'Login History',
};

// Profile, Address, Nationalities and Languages all submit through the
// same PUT /account endpoint (one validation call on the backend) —
// mirroring UserForm.tsx's RECORD_TABS pattern so no tab risks sending a
// partial payload that fails another tab's required fields.
const PROFILE_TABS: Tab[] = ['profile', 'address', 'nationalities', 'languages'];

const FIELD_TAB: Record<string, Tab> = {
  first_name: 'profile',
  last_name: 'profile',
  mobile: 'profile',
  gender: 'profile',
  date_of_birth: 'profile',
  address: 'address',
  nationalities: 'nationalities',
  languages: 'languages',
};

interface ProfileFormState {
  first_name: string;
  last_name: string;
  mobile: string;
  address: string;
  gender: string;
  date_of_birth: string;
  nationalities: string[];
  languages: string[];
}

interface UserIdentityRow {
  id: number;
  identity_type: string;
  number: string;
  issuer: string | null;
  expiry_date: string | null;
}

const emptyIdentityForm = { identity_type: '', number: '', issuer: '', expiry_date: '' };

const emptyForm: ProfileFormState = {
  first_name: '',
  last_name: '',
  mobile: '',
  address: '',
  gender: '',
  date_of_birth: '',
  nationalities: [],
  languages: [],
};

// Self-service session shape — carries is_current (the admin-facing
// /users/{id}/sessions endpoint in UserForm.tsx doesn't need it, since an
// admin viewing someone else's sessions is never looking at "this device").
interface Session {
  id: number;
  name: string;
  created_at: string;
  last_used_at: string | null;
  is_current: boolean;
}

interface HistoryPage {
  data: { id: number; created_at: string; ip_address: string | null; user_agent: string | null }[];
  current_page: number;
  last_page: number;
  total: number;
}

type FormErrors = Record<string, string[]>;

type TwoFactorStep = 'idle' | 'confirming' | 'recovery-codes';

export function Account() {
  const { user, refreshUser } = useAuth();
  const { lists } = useConfig();
  const confirmAction = useConfirm();
  const [tab, setTab] = useState<Tab>('profile');

  // `user` is cached in AuthContext from login/app-load and only re-synced
  // on a full reload — without this, a role change made elsewhere (e.g.
  // System > Users) wouldn't show up here until the next login, even
  // though UserDetail.tsx's own fresh fetch already reflects it.
  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const [form, setForm] = useState<ProfileFormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [identities, setIdentities] = useState<UserIdentityRow[]>([]);
  const [identityForm, setIdentityForm] = useState(emptyIdentityForm);
  const [identityErrors, setIdentityErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (!user) return;
    setForm({
      first_name: user.first_name,
      last_name: user.last_name,
      mobile: user.mobile ?? '',
      address: user.address ?? '',
      gender: user.gender ?? '',
      date_of_birth: user.date_of_birth ?? '',
      nationalities: user.nationalities ?? [],
      languages: user.languages ?? [],
    });
    setIdentities(user.identities ?? []);
  }, [user]);

  const addIdentity = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const res = await api.post<UserIdentityRow[]>('/account/identities', identityForm);
      setIdentities(res.data);
      setIdentityForm(emptyIdentityForm);
      setIdentityErrors({});
    } catch (err) {
      setIdentityErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const removeIdentity = async (row: UserIdentityRow) => {
    if (!(await confirmAction('Remove this identity document?'))) return;
    const res = await api.delete<UserIdentityRow[]>(`/user-identities/${row.id}`);
    setIdentities(res.data);
  };

  const [photoError, setPhotoError] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [passwordForm, setPasswordForm] = useState({ current_password: '', password: '', password_confirmation: '' });
  const [passwordErrors, setPasswordErrors] = useState<FormErrors>({});
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);

  const [sessions, setSessions] = useState<Session[] | null>(null);
  const [revoking, setRevoking] = useState(false);

  const [twoFactorStep, setTwoFactorStep] = useState<TwoFactorStep>('idle');
  const [twoFactorSecret, setTwoFactorSecret] = useState('');
  const [twoFactorQrCode, setTwoFactorQrCode] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorErrors, setTwoFactorErrors] = useState<FormErrors>({});
  const [twoFactorBusy, setTwoFactorBusy] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [showDisable2fa, setShowDisable2fa] = useState(false);
  const [disable2faPassword, setDisable2faPassword] = useState('');
  const [disable2faErrors, setDisable2faErrors] = useState<FormErrors>({});
  const [disabling2fa, setDisabling2fa] = useState(false);

  const [notifySaving, setNotifySaving] = useState(false);

  const [history, setHistory] = useState<HistoryPage | null>(null);
  const [historyPage, setHistoryPage] = useState(1);

  useEffect(() => {
    if (tab === 'security' && !sessions) {
      api.get<Session[]>('/account/sessions').then((res) => setSessions(res.data));
    }
  }, [tab, sessions]);

  useEffect(() => {
    if (tab === 'login-history') {
      api.get<HistoryPage>('/account/login-history', { params: { page: historyPage } }).then((res) => setHistory(res.data));
    }
  }, [tab, historyPage]);

  const set =
    (key: keyof ProfileFormState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setSaved(false);
    const payload = {
      first_name: form.first_name,
      last_name: form.last_name,
      mobile: form.mobile || null,
      address: form.address || null,
      gender: form.gender || null,
      date_of_birth: form.date_of_birth || null,
      nationalities: form.nationalities,
      languages: form.languages,
    };
    try {
      await api.put('/account', payload);
      await refreshUser();
      setErrors({});
      setSaved(true);
    } catch (err) {
      const fieldErrors: FormErrors =
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] };
      setErrors(fieldErrors);
      const firstField = Object.keys(fieldErrors).find((k) => k !== 'general');
      if (firstField && FIELD_TAB[firstField]) setTab(FIELD_TAB[firstField]);
    } finally {
      setSaving(false);
    }
  };

  const pickPhoto = () => fileInputRef.current?.click();

  const onPhotoSelected = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setPhotoError('');
    const data = new FormData();
    data.append('photo', file);
    setUploadingPhoto(true);
    try {
      await api.post('/account/photo', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      await refreshUser();
    } catch (err) {
      setPhotoError(err instanceof ApiError ? err.fieldError('photo') ?? err.message : 'Upload failed.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = async () => {
    if (!(await confirmAction('Remove this photo?', { confirmLabel: 'Remove' }))) return;
    setUploadingPhoto(true);
    try {
      await api.delete('/account/photo');
      await refreshUser();
    } finally {
      setUploadingPhoto(false);
    }
  };

  const savePassword = async (e: FormEvent) => {
    e.preventDefault();
    if (passwordSaving) return;
    setPasswordSaving(true);
    setPasswordSaved(false);
    try {
      await api.put('/account/password', passwordForm);
      setPasswordForm({ current_password: '', password: '', password_confirmation: '' });
      setPasswordErrors({});
      setPasswordSaved(true);
    } catch (err) {
      setPasswordErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    } finally {
      setPasswordSaving(false);
    }
  };

  const revokeSession = async (session: Session) => {
    if (!(await confirmAction('Sign out of this session?', { confirmLabel: 'Sign out' }))) return;
    setRevoking(true);
    try {
      await api.delete(`/account/sessions/${session.id}`);
      const res = await api.get<Session[]>('/account/sessions');
      setSessions(res.data);
    } finally {
      setRevoking(false);
    }
  };

  const revokeOtherSessions = async () => {
    if (!(await confirmAction('Sign out of all other sessions?', { confirmLabel: 'Sign out' }))) return;
    setRevoking(true);
    try {
      await api.post('/account/sessions/revoke-others');
      const res = await api.get<Session[]>('/account/sessions');
      setSessions(res.data);
    } finally {
      setRevoking(false);
    }
  };

  const startTwoFactorEnable = async () => {
    setTwoFactorBusy(true);
    setTwoFactorErrors({});
    try {
      const res = await api.post<{ secret: string; qr_code: string }>('/account/two-factor/enable');
      setTwoFactorSecret(res.data.secret);
      setTwoFactorQrCode(res.data.qr_code);
      setTwoFactorCode('');
      setTwoFactorStep('confirming');
    } finally {
      setTwoFactorBusy(false);
    }
  };

  const cancelTwoFactorEnable = () => {
    setTwoFactorStep('idle');
    setTwoFactorSecret('');
    setTwoFactorQrCode('');
    setTwoFactorCode('');
    setTwoFactorErrors({});
  };

  const confirmTwoFactor = async (e: FormEvent) => {
    e.preventDefault();
    if (twoFactorBusy) return;
    setTwoFactorBusy(true);
    try {
      const res = await api.post<{ recovery_codes: string[] }>('/account/two-factor/confirm', { code: twoFactorCode });
      setRecoveryCodes(res.data.recovery_codes);
      setTwoFactorStep('recovery-codes');
      setTwoFactorErrors({});
      await refreshUser();
    } catch (err) {
      setTwoFactorErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Verification failed.'] }
      );
    } finally {
      setTwoFactorBusy(false);
    }
  };

  const finishTwoFactorSetup = () => {
    setTwoFactorStep('idle');
    setTwoFactorSecret('');
    setTwoFactorQrCode('');
    setTwoFactorCode('');
    setRecoveryCodes([]);
  };

  const submitDisableTwoFactor = async (e: FormEvent) => {
    e.preventDefault();
    if (disabling2fa) return;
    setDisabling2fa(true);
    try {
      await api.post('/account/two-factor/disable', { password: disable2faPassword });
      setShowDisable2fa(false);
      setDisable2faPassword('');
      setDisable2faErrors({});
      await refreshUser();
    } catch (err) {
      setDisable2faErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Failed to disable.'] }
      );
    } finally {
      setDisabling2fa(false);
    }
  };

  const toggleNotify = async (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setNotifySaving(true);
    try {
      await api.put('/account/notifications', { notify_security_emails: checked });
      await refreshUser();
    } finally {
      setNotifySaving(false);
    }
  };

  return (
    <>
      <div className="page-header">
        <h1>Account</h1>
      </div>
      <p className="muted">Manage your own profile.</p>

      <div className="toolbar">
        <div className="tab-group">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              className={tab === t ? 'tab active' : 'tab'}
              onClick={() => setTab(t)}
            >
              {TAB_LABELS[t]}
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        {PROFILE_TABS.includes(tab) && (
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}

            {tab === 'profile' && (
              <>
                <div className="span-2 icon-upload">
                  <img src={user?.photo_url ?? vnpfLogo} alt="" className="icon-upload-preview" />
                  <div className="icon-upload-actions">
                    <button type="button" className="btn btn-ghost" onClick={pickPhoto} disabled={uploadingPhoto}>
                      {uploadingPhoto ? 'Uploading…' : 'Upload photo'}
                    </button>
                    {user?.photo_url && (
                      <button
                        type="button"
                        className="btn btn-danger-ghost"
                        onClick={removePhoto}
                        disabled={uploadingPhoto}
                      >
                        Remove photo
                      </button>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={onPhotoSelected}
                    hidden
                  />
                  {photoError && <div className="alert alert-error icon-upload-error">{photoError}</div>}
                </div>

                <div className="detail-item">
                  <span className="detail-item-label">Email</span>
                  <span className="detail-item-value">{user?.email}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-item-label">Roles</span>
                  <span className="detail-item-value">{user?.roles.map(roleLabel).join(', ') || 'None'}</span>
                </div>

                <label>
                  First name *
                  <input value={form.first_name} onChange={set('first_name')} required />
                  {errors.first_name && <small className="error">{errors.first_name[0]}</small>}
                </label>
                <label>
                  Last name *
                  <input value={form.last_name} onChange={set('last_name')} required />
                  {errors.last_name && <small className="error">{errors.last_name[0]}</small>}
                </label>
                <label>
                  Mobile
                  <input value={form.mobile} onChange={set('mobile')} maxLength={20} />
                  {errors.mobile && <small className="error">{errors.mobile[0]}</small>}
                </label>
                <label>
                  Gender
                  <select value={form.gender} onChange={set('gender')}>
                    <option value="">—</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                  {errors.gender && <small className="error">{errors.gender[0]}</small>}
                </label>
                <label>
                  Date of birth
                  <input type="date" value={form.date_of_birth} onChange={set('date_of_birth')} />
                  {errors.date_of_birth && <small className="error">{errors.date_of_birth[0]}</small>}
                </label>
              </>
            )}

            {tab === 'address' && (
              <label className="span-2">
                Address
                <textarea rows={3} value={form.address} onChange={set('address')} />
                {errors.address && <small className="error">{errors.address[0]}</small>}
              </label>
            )}

            {tab === 'nationalities' && (
              <div className="span-2">
                <MultiListSelect
                  listKey="nationality"
                  value={form.nationalities}
                  onChange={(nationalities) => setForm({ ...form, nationalities })}
                />
                {errors.nationalities && <small className="error">{errors.nationalities[0]}</small>}
              </div>
            )}

            {tab === 'languages' && (
              <div className="span-2">
                <MultiListSelect
                  listKey="language"
                  value={form.languages}
                  onChange={(languages) => setForm({ ...form, languages })}
                />
                {errors.languages && <small className="error">{errors.languages[0]}</small>}
              </div>
            )}

            <div className="form-actions span-2">
              {saved && <span className="muted">Saved.</span>}
              <button className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        )}

        {tab === 'identities' && (
          <>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Number</th>
                    <th>Issuer</th>
                    <th>Expiry</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {identities.map((row) => (
                    <tr key={row.id}>
                      <td>{labelFor(lists, 'identity_type', row.identity_type)}</td>
                      <td>{row.number}</td>
                      <td className="muted">{row.issuer ?? '—'}</td>
                      <td className="muted">{row.expiry_date ?? '—'}</td>
                      <td className="right">
                        <IconButton icon={IconTrash} label="Remove" variant="danger-ghost" onClick={() => removeIdentity(row)} />
                      </td>
                    </tr>
                  ))}
                  {identities.length === 0 && (
                    <tr>
                      <td colSpan={5} className="muted center">
                        No identity documents yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <form onSubmit={addIdentity} className="form-grid" style={{ marginTop: 16 }}>
              {identityErrors.general && <div className="alert alert-error span-2">{identityErrors.general[0]}</div>}
              <label>
                Identity Type *
                <select
                  value={identityForm.identity_type}
                  onChange={(e) => setIdentityForm({ ...identityForm, identity_type: e.target.value })}
                  required
                >
                  <option value="">—</option>
                  {listOptions(lists, 'identity_type').map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {identityErrors.identity_type && <small className="error">{identityErrors.identity_type[0]}</small>}
              </label>
              <label>
                Number *
                <input
                  value={identityForm.number}
                  onChange={(e) => setIdentityForm({ ...identityForm, number: e.target.value })}
                  required
                />
                {identityErrors.number && <small className="error">{identityErrors.number[0]}</small>}
              </label>
              <label>
                Issuer
                <input value={identityForm.issuer} onChange={(e) => setIdentityForm({ ...identityForm, issuer: e.target.value })} />
                {identityErrors.issuer && <small className="error">{identityErrors.issuer[0]}</small>}
              </label>
              <label>
                Expiry Date
                <input
                  type="date"
                  value={identityForm.expiry_date}
                  onChange={(e) => setIdentityForm({ ...identityForm, expiry_date: e.target.value })}
                />
                {identityErrors.expiry_date && <small className="error">{identityErrors.expiry_date[0]}</small>}
              </label>
              <div className="form-actions span-2">
                <button className="btn btn-primary">Add identity</button>
              </div>
            </form>
          </>
        )}

        {tab === 'password' && (
          <form onSubmit={savePassword} className="form-grid">
            {passwordErrors.general && <div className="alert alert-error span-2">{passwordErrors.general[0]}</div>}
            <label>
              Current password *
              <input
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                required
              />
              {passwordErrors.current_password && <small className="error">{passwordErrors.current_password[0]}</small>}
            </label>
            <div />
            <label>
              New password *
              <input
                type="password"
                value={passwordForm.password}
                onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
                required
                minLength={8}
              />
              {passwordErrors.password && <small className="error">{passwordErrors.password[0]}</small>}
            </label>
            <label>
              Confirm new password *
              <input
                type="password"
                value={passwordForm.password_confirmation}
                onChange={(e) => setPasswordForm({ ...passwordForm, password_confirmation: e.target.value })}
                required
                minLength={8}
              />
            </label>
            <div className="form-actions span-2">
              {passwordSaved && <span className="muted">Password changed.</span>}
              <button className="btn btn-primary" disabled={passwordSaving}>
                {passwordSaving ? 'Saving…' : 'Change password'}
              </button>
            </div>
          </form>
        )}

        {tab === 'security' && (
          <>
            <div className="card-header">
              <h2>Two-factor authentication</h2>
            </div>
            <div className="span-2" style={{ marginBottom: 24 }}>
              {twoFactorStep === 'idle' && user?.two_factor_enabled && (
                <>
                  <p className="muted">
                    Two-factor authentication is <strong>enabled</strong> on your account.
                  </p>
                  <button
                    type="button"
                    className="btn btn-danger-ghost"
                    onClick={() => {
                      setDisable2faErrors({});
                      setDisable2faPassword('');
                      setShowDisable2fa(true);
                    }}
                  >
                    Disable two-factor authentication
                  </button>
                </>
              )}

              {twoFactorStep === 'idle' && !user?.two_factor_enabled && (
                <>
                  <p className="muted">
                    Two-factor authentication is <strong>disabled</strong> on your account.
                  </p>
                  <button type="button" className="btn btn-primary" onClick={startTwoFactorEnable} disabled={twoFactorBusy}>
                    {twoFactorBusy ? 'Starting…' : 'Enable two-factor authentication'}
                  </button>
                </>
              )}

              {twoFactorStep === 'confirming' && (
                <form onSubmit={confirmTwoFactor} className="form-grid">
                  <div className="span-2">
                    <p className="muted">Scan this code with your authenticator app, then enter the 6-digit code it shows.</p>
                    <img src={twoFactorQrCode} alt="Two-factor setup QR code" style={{ display: 'block', marginBottom: 8 }} />
                    <p className="muted">
                      Can't scan? Enter this key manually: <code>{twoFactorSecret}</code>
                    </p>
                  </div>
                  <label>
                    Authentication code *
                    <input
                      type="text"
                      inputMode="numeric"
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value)}
                      required
                      autoFocus
                    />
                    {twoFactorErrors.code && <small className="error">{twoFactorErrors.code[0]}</small>}
                  </label>
                  <div className="form-actions span-2">
                    <button type="button" className="btn btn-ghost" onClick={cancelTwoFactorEnable}>
                      Cancel
                    </button>
                    <button className="btn btn-primary" disabled={twoFactorBusy}>
                      {twoFactorBusy ? 'Verifying…' : 'Confirm'}
                    </button>
                  </div>
                </form>
              )}

              {twoFactorStep === 'recovery-codes' && (
                <div>
                  <p>
                    <strong>Save these recovery codes now — they won't be shown again.</strong> Each can be used once to sign
                    in if you lose access to your authenticator app.
                  </p>
                  <div className="form-grid" style={{ fontFamily: 'monospace', marginBottom: 16 }}>
                    {recoveryCodes.map((code) => (
                      <div key={code}>{code}</div>
                    ))}
                  </div>
                  <button type="button" className="btn btn-primary" onClick={finishTwoFactorSetup}>
                    Done
                  </button>
                </div>
              )}
            </div>

            <div className="card-header">
              <h2>Active sessions</h2>
              <button type="button" className="btn btn-ghost" onClick={revokeOtherSessions} disabled={revoking}>
                Sign out of other sessions
              </button>
            </div>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Device</th>
                    <th>Signed in</th>
                    <th>Last used</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sessions?.map((s) => (
                    <tr key={s.id}>
                      <td>
                        {s.name} {s.is_current && <span className="badge badge-neutral">This device</span>}
                      </td>
                      <td>{formatDateTime(s.created_at)}</td>
                      <td className="muted">{s.last_used_at ? formatDateTime(s.last_used_at) : 'Never'}</td>
                      <td className="right">
                        {!s.is_current && (
                          <IconButton
                            icon={IconTrash}
                            label="Sign out"
                            variant="danger-ghost"
                            onClick={() => revokeSession(s)}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                  {sessions?.length === 0 && (
                    <tr>
                      <td colSpan={4} className="muted center">
                        No active sessions.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === 'notifications' && (
          <div className="form-grid">
            <label className="span-2 checkbox-label">
              <input
                type="checkbox"
                checked={user?.notify_security_emails ?? false}
                onChange={toggleNotify}
                disabled={notifySaving}
              />
              Email me when my password is changed
            </label>
            <small className="muted span-2">Security notifications are sent to your account email.</small>
          </div>
        )}

        {tab === 'login-history' && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>IP Address</th>
                  <th>Device</th>
                </tr>
              </thead>
              <tbody>
                {history?.data.map((h) => (
                  <tr key={h.id}>
                    <td>{formatDateTime(h.created_at)}</td>
                    <td className="muted">{h.ip_address ?? '—'}</td>
                    <td className="muted">{h.user_agent ?? '—'}</td>
                  </tr>
                ))}
                {history && history.data.length === 0 && (
                  <tr>
                    <td colSpan={3} className="muted center">
                      No login history yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <Pagination meta={history} onPage={setHistoryPage} />
          </div>
        )}
      </div>

      {showDisable2fa && (
        <Modal title="Disable two-factor authentication" onClose={() => setShowDisable2fa(false)}>
          <form onSubmit={submitDisableTwoFactor} className="form-grid">
            {disable2faErrors.general && <div className="alert alert-error span-2">{disable2faErrors.general[0]}</div>}
            <label className="span-2">
              Current password
              <input
                type="password"
                value={disable2faPassword}
                onChange={(e) => setDisable2faPassword(e.target.value)}
                required
                autoFocus
              />
              {disable2faErrors.password && <small className="error">{disable2faErrors.password[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setShowDisable2fa(false)}>
                Cancel
              </button>
              <button className="btn btn-danger-ghost" disabled={disabling2fa}>
                {disabling2fa ? 'Disabling…' : 'Disable'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
